import PropTypes from 'prop-types';
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import { stripHtml, sumObjectValues } from '../../../utils/util/util';
import Heatmap from './Heatmap/Heatmap';
import {
  FormatValueResult,
  GenerateCsvData,
  GetValueFunc,
} from './Heatmap/types';

const COLOR_SCALE = (value, data) => {
  if (typeof value !== 'number') {
    return '';
  }
  const baseline = Math.round(100 / data.numOptions);
  if (value > 2 * baseline) {
    return {
      background: '#5588e8',
      text: '#12263f',
    };
  }
  if (value > baseline) {
    return {
      background: '#9fb3f2',
      text: '#12263f',
    };
  }
  return {
    background: '#dae0fa',
    text: '#12263f',
  };
};

const FORMAT_VALUE = (value) => {
  // No responses
  if (typeof value !== 'number') {
    return '-';
  }

  return Math.round(100 * value);
};

const formatValueResultToNumber = (val: FormatValueResult): number => {
  return val === '-' ? 0 : val;
};

const generateCsvData: GenerateCsvData = (
  segmentValues: string[],
  frozenRowData: object[],
  unfrozenRowData: object[],
  formatValue: GetValueFunc,
  allColumnHeaderText?: string
): any[][] => {
  const allHeader = allColumnHeaderText ?? 'All';
  const headers = [
    'Question',
    'Factor',
    'Option',
    allHeader,
    ...segmentValues,
    ...segmentValues.map((x) => `${x} (Delta to ${allHeader})`),
    ...segmentValues.map((x) => `${x} (# of responses)`),
  ];

  const unfrozenDict = unfrozenRowData.reduce((acc, curr) => {
    // @ts-expect-error
    acc[curr._choice] = curr;
    return acc;
  }, {});

  return [
    headers,
    ...frozenRowData.map((_frozen) => {
      // @ts-expect-error
      const allValueTxt = formatValue(_frozen?.all?.responses.value);

      const allValueNum = formatValueResultToNumber(allValueTxt);

      return [
        // @ts-expect-error
        stripHtml(_frozen._name),
        // @ts-expect-error
        _frozen._factor ?? 'No factor',
        // @ts-expect-error
        _frozen._choice,
        allValueNum.toString(),
        ...segmentValues.map((sv) =>
          formatValue(
            // @ts-expect-error
            unfrozenDict[_frozen._choice]?.[sv]?.responses?.value
          ).toString()
        ),
        ...segmentValues.map((sv) => {
          const v = formatValue(
            // @ts-expect-error
            unfrozenDict[_frozen._choice]?.[sv]?.responses?.value
          );
          const vNum = formatValueResultToNumber(v);
          const delta = vNum - allValueNum;
          return delta.toString();
        }),
        ...segmentValues.map((sv) => {
          const v =
            // @ts-expect-error
            unfrozenDict[_frozen._choice]?.[sv]?.responses?.count ?? 0;
          return v?.toString();
        }),
      ];
    }),
  ];
};

const MultipleChoiceHeatmap: FC<Props> = (props) => {
  const { formatMessage } = useIntl();
  const emptyFactorDisplay = useMemo(() => {
    return formatMessage({
      id: 'app.views.widgets.charts.multiple_choice_heatmap.no_factor_header',
      defaultMessage: '(No factor)',
    });
  }, [formatMessage]);
  const getColor = useMemo(() => {
    if (props.colorScale) {
      return props.colorScale;
    }
    return COLOR_SCALE;
  }, [props.colorScale]);

  const getValue = useMemo(() => {
    if (props.formatValue) {
      return props.formatValue;
    }
    return FORMAT_VALUE;
  }, [props.formatValue]);

  const dataByFactor = useMemo(() => {
    const groupedByFactor = props.data?.reduce((acc: object, d) => {
      // @ts-expect-error
      const factor = d.factor ?? '';
      if (!acc[factor]) {
        acc[factor] = [];
      }

      acc[factor].push({
        q: [d],
        frozenRowData: (segment) => {
          if (!segment) {
            return [];
          }
          // @ts-expect-error
          return d.options?.map((o) => {
            // @ts-expect-error
            const responses = d.all_by_breakdown?.[segment]?.responses;
            const count = responses?.[o.id] || 0;
            const total = sumObjectValues(responses);
            return {
              all: {
                responses: { value: count / total, count: count },
                // @ts-expect-error
                ...(d.all_by_breakdown?.[segment]?.excluded_filter_values
                  ? {
                      excluded_filter_values:
                        // @ts-expect-error
                        d.all_by_breakdown[segment]?.excluded_filter_values,
                    }
                  : {}),
              },
              count: count,
              name: o?.name,
              // @ts-expect-error
              numOptions: d.options.length,
              customPrefix: 'frozen',
              // @ts-expect-error
              _name: d?.displayName,
              _choice: o?.name,
              _segment: segment,
              // @ts-expect-error
              _factor: d?.factor,
            };
          });
        },
        unfrozenRowData: (segment) => {
          if (!segment) {
            return [];
          }
          // @ts-expect-error
          return d.options?.map((o) => {
            // @ts-expect-error
            const formattedResponses = { _name: d.displayName };
            // @ts-expect-error
            const segmentedResponses = d.responses?.[segment];

            for (const _segment in segmentedResponses) {
              const _responses = segmentedResponses[_segment];
              const _count = _responses?.responses?.[o.id] || 0;
              const total = sumObjectValues(_responses?.responses);
              formattedResponses[_segment] = {
                responses: {
                  count: _count,
                  value: total === 0 ? total : _count / total,
                },
                ...(segment && _segment === '_others'
                  ? {
                      included_filter_values:
                        // @ts-expect-error
                        d.responses?.[segment]?.[_segment]
                          ?.included_filter_values ?? [],
                    }
                  : {}),
              };
            }

            return {
              // @ts-expect-error
              numOptions: d.options.length,
              _segment: segment,
              _choice: o?.name,
              ...formattedResponses,
            };
          });
        },
      });
      return acc;
    }, {});

    return groupedByFactor;
  }, [props.data]);

  const factorKeys = useMemo(() => {
    return Object.keys(dataByFactor).sort((a, b) => {
      // Sort by factor, with "" as the last factor
      if (a === '') return 1;
      if (b === '') return -1;
      return a.localeCompare(b);
    });
  }, [dataByFactor]);

  const hasFactorHeaders = useMemo(() => {
    return factorKeys.length > 0 && factorKeys.some((key) => key !== '');
  }, [factorKeys]);

  return (
    <>
      {factorKeys.map((factor, fi) => {
        const factorData = dataByFactor[factor];

        return (
          <React.Fragment key={fi}>
            {hasFactorHeaders && (
              <div className={'m-4'}>
                <Row key={factor}>
                  <Col className="fw-bold text-dark fs-4">
                    {factor || emptyFactorDisplay}
                  </Col>
                </Row>
              </div>
            )}
            {factorData.map((data, idx) => {
              const frozenRowData = data.frozenRowData;
              const unFrozenRowData = data.unfrozenRowData;
              return (
                <Heatmap
                  key={idx}
                  data={data.q}
                  colorScale={getColor}
                  formatValue={getValue}
                  frozenRowData={frozenRowData}
                  unFrozenRowData={unFrozenRowData}
                  isMultipleChoice={true}
                  showTitle={true}
                  generateCsvData={generateCsvData}
                />
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};

const MultipleChoiceHeatmap_propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  colorScale: PropTypes.func,
  formatValue: PropTypes.func,
};

type Props = PropTypes.InferProps<typeof MultipleChoiceHeatmap_propTypes>;

export default React.memo(MultipleChoiceHeatmap);
